import { Dispatch, SetStateAction } from 'react'
import { Trans } from '@lingui/macro'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import { FieldErrors } from 'react-hook-form'

import { alpha, styled } from '@mui/material/styles'
import LoadingButton from '@mui/lab/LoadingButton'
import Menu from '@mui/material/Menu'
import Stack from '@mui/material/Stack'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { useShowDMP } from '@/features/dmp-connect'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen'

import { CheckInsAndIdentity } from './CheckInsAndIdentity'
import { CreatePatientAccount } from './CreatePatientAccount'
import { DeletePatient } from './DeletePatient'
import { DownloadConsent } from './DownloadConsent'
import { GetIns } from './GetIns'
import { OpenDMP } from './OpenDMP'
import { OpenMSSante } from './OpenMSSante'
import { StartLocalConsultation } from './StartLocalConsultation'

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}))

type ActionsProps = {
  patientId: string
  setFormErrors: Dispatch<SetStateAction<FieldErrors<PatientProfileFormData>>>
}
export function Actions({ patientId, setFormErrors }: ActionsProps) {
  const { isINSEnabled } = useIsINSEnabled()
  const isLargeScreen = useIsLargeScreen()
  const showDMP = useShowDMP(patientId)

  return (
    <>
      {isLargeScreen ? (
        <Stack spacing={1} alignItems="flex-end">
          <Stack direction="row" spacing={2}>
            {isINSEnabled && <GetIns patientId={patientId} setFormErrors={setFormErrors} />}
            {isINSEnabled && <CheckInsAndIdentity patientId={patientId} />}
            <CreatePatientAccount patientId={patientId} />
            <StartLocalConsultation patientId={patientId} />
            <DownloadConsent patientId={patientId} />
            <DeletePatient patientId={patientId} />
          </Stack>
          {showDMP && (
            <Stack direction="row" spacing={2}>
              <OpenDMP patientId={patientId} />
              <OpenMSSante patientId={patientId} />
            </Stack>
          )}
        </Stack>
      ) : (
        <PopupState variant="popover" popupId="more-button">
          {(popupState) => (
            <>
              <LoadingButton variant="contained" disableElevation endIcon={<KeyboardArrowDownIcon />} {...bindTrigger(popupState)}>
                <Trans>Action</Trans>
              </LoadingButton>
              <StyledMenu
                {...bindMenu(popupState)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {isINSEnabled && <GetIns onClose={popupState.close} patientId={patientId} setFormErrors={setFormErrors} />}
                {isINSEnabled && <CheckInsAndIdentity onClose={popupState.close} patientId={patientId} />}
                <CreatePatientAccount onClose={popupState.close} patientId={patientId} />
                <StartLocalConsultation onClose={popupState.close} patientId={patientId} />
                {showDMP && <OpenDMP patientId={patientId} onClose={popupState.close} />}
                {showDMP && <OpenMSSante patientId={patientId} onClose={popupState.close} />}
                <DownloadConsent onClose={popupState.close} patientId={patientId} />
                <DeletePatient patientId={patientId} />
              </StyledMenu>
            </>
          )}
        </PopupState>
      )}
    </>
  )
}
